import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// UI State interface
interface UiState {
  // Layout
  sidebarOpen: boolean
  sidebarCollapsed: boolean
  
  // Theme
  theme: 'light' | 'dark' | 'system'
  
  // Loading states
  globalLoading: boolean
  loadingMessage: string | null
  
  // Notifications
  notifications: Array<{
    id: string
    type: 'success' | 'error' | 'warning' | 'info'
    title: string
    message: string
    duration?: number
    persistent?: boolean
    action?: {
      label: string
      handler: () => void
    }
  }>
  
  // Modals and dialogs
  activeModal: string | null
  modalData: Record<string, any>
  
  // Page-specific UI states
  pageStates: Record<string, any>
  
  // Search
  globalSearchOpen: boolean
  globalSearchQuery: string
  globalSearchResults: Array<{
    id: string
    title: string
    subtitle?: string
    category: string
    url: string
    icon?: string
  }>
  globalSearchLoading: boolean
  
  // Breadcrumbs
  breadcrumbs: Array<{
    label: string
    url?: string
    icon?: string
  }>
  
  // Navigation
  navigationHistory: string[]
  
  // User preferences
  userPreferences: {
    language: string
    dateFormat: string
    timeFormat: string
    currency: string
    numberFormat: string
    pageSize: number
    compactMode: boolean
    showTutorials: boolean
  }
  
  // Tour and tutorials
  activeTour: string | null
  tourStep: number
  toursCompleted: string[]
  
  // Responsive design
  deviceType: 'mobile' | 'tablet' | 'desktop'
  screenSize: {
    width: number
    height: number
  }
  
  // Performance monitoring
  performanceMetrics: {
    pageLoadTime: number
    apiResponseTimes: Record<string, number>
    renderTimes: Record<string, number>
  }
}

// Initial state
const initialState: UiState = {
  sidebarOpen: true,
  sidebarCollapsed: false,
  theme: 'system',
  globalLoading: false,
  loadingMessage: null,
  notifications: [],
  activeModal: null,
  modalData: {},
  pageStates: {},
  globalSearchOpen: false,
  globalSearchQuery: '',
  globalSearchResults: [],
  globalSearchLoading: false,
  breadcrumbs: [],
  navigationHistory: [],
  userPreferences: {
    language: 'en',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    currency: 'SAR',
    numberFormat: 'en-US',
    pageSize: 20,
    compactMode: false,
    showTutorials: true
  },
  activeTour: null,
  tourStep: 0,
  toursCompleted: [],
  deviceType: 'desktop',
  screenSize: {
    width: 1920,
    height: 1080
  },
  performanceMetrics: {
    pageLoadTime: 0,
    apiResponseTimes: {},
    renderTimes: {}
  }
}

// Create slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Layout actions
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload
    },
    toggleSidebarCollapsed: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload
    },
    
    // Theme actions
    setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
      state.theme = action.payload
    },
    
    // Loading actions
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.globalLoading = action.payload
      if (!action.payload) {
        state.loadingMessage = null
      }
    },
    setLoadingMessage: (state, action: PayloadAction<string | null>) => {
      state.loadingMessage = action.payload
    },
    
    // Notification actions
    addNotification: (state, action: PayloadAction<Omit<UiState['notifications'][0], 'id'>>) => {
      const notification = {
        ...action.payload,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9)
      }
      state.notifications.push(notification)
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload)
    },
    clearNotifications: (state) => {
      state.notifications = []
    },
    
    // Modal actions
    openModal: (state, action: PayloadAction<{ modal: string; data?: any }>) => {
      state.activeModal = action.payload.modal
      state.modalData = action.payload.data || {}
    },
    closeModal: (state) => {
      state.activeModal = null
      state.modalData = {}
    },
    setModalData: (state, action: PayloadAction<Record<string, any>>) => {
      state.modalData = { ...state.modalData, ...action.payload }
    },
    
    // Page state actions
    setPageState: (state, action: PayloadAction<{ page: string; data: any }>) => {
      state.pageStates[action.payload.page] = action.payload.data
    },
    updatePageState: (state, action: PayloadAction<{ page: string; data: any }>) => {
      state.pageStates[action.payload.page] = {
        ...state.pageStates[action.payload.page],
        ...action.payload.data
      }
    },
    clearPageState: (state, action: PayloadAction<string>) => {
      delete state.pageStates[action.payload]
    },
    
    // Search actions
    setGlobalSearchOpen: (state, action: PayloadAction<boolean>) => {
      state.globalSearchOpen = action.payload
      if (!action.payload) {
        state.globalSearchQuery = ''
        state.globalSearchResults = []
      }
    },
    setGlobalSearchQuery: (state, action: PayloadAction<string>) => {
      state.globalSearchQuery = action.payload
    },
    setGlobalSearchResults: (state, action: PayloadAction<UiState['globalSearchResults']>) => {
      state.globalSearchResults = action.payload
    },
    setGlobalSearchLoading: (state, action: PayloadAction<boolean>) => {
      state.globalSearchLoading = action.payload
    },
    
    // Breadcrumb actions
    setBreadcrumbs: (state, action: PayloadAction<UiState['breadcrumbs']>) => {
      state.breadcrumbs = action.payload
    },
    addBreadcrumb: (state, action: PayloadAction<UiState['breadcrumbs'][0]>) => {
      state.breadcrumbs.push(action.payload)
    },
    removeBreadcrumb: (state, action: PayloadAction<number>) => {
      state.breadcrumbs.splice(action.payload, 1)
    },
    clearBreadcrumbs: (state) => {
      state.breadcrumbs = []
    },
    
    // Navigation actions
    addToNavigationHistory: (state, action: PayloadAction<string>) => {
      state.navigationHistory.push(action.payload)
      // Keep only last 50 entries
      if (state.navigationHistory.length > 50) {
        state.navigationHistory = state.navigationHistory.slice(-50)
      }
    },
    clearNavigationHistory: (state) => {
      state.navigationHistory = []
    },
    
    // User preferences actions
    setUserPreferences: (state, action: PayloadAction<Partial<UiState['userPreferences']>>) => {
      state.userPreferences = { ...state.userPreferences, ...action.payload }
    },
    resetUserPreferences: (state) => {
      state.userPreferences = initialState.userPreferences
    },
    
    // Tour actions
    startTour: (state, action: PayloadAction<string>) => {
      state.activeTour = action.payload
      state.tourStep = 0
    },
    nextTourStep: (state) => {
      state.tourStep += 1
    },
    previousTourStep: (state) => {
      if (state.tourStep > 0) {
        state.tourStep -= 1
      }
    },
    setTourStep: (state, action: PayloadAction<number>) => {
      state.tourStep = action.payload
    },
    completeTour: (state) => {
      if (state.activeTour) {
        state.toursCompleted.push(state.activeTour)
        state.activeTour = null
        state.tourStep = 0
      }
    },
    exitTour: (state) => {
      state.activeTour = null
      state.tourStep = 0
    },
    
    // Device actions
    setDeviceType: (state, action: PayloadAction<'mobile' | 'tablet' | 'desktop'>) => {
      state.deviceType = action.payload
    },
    setScreenSize: (state, action: PayloadAction<{ width: number; height: number }>) => {
      state.screenSize = action.payload
    },
    
    // Performance actions
    setPageLoadTime: (state, action: PayloadAction<number>) => {
      state.performanceMetrics.pageLoadTime = action.payload
    },
    setApiResponseTime: (state, action: PayloadAction<{ endpoint: string; time: number }>) => {
      state.performanceMetrics.apiResponseTimes[action.payload.endpoint] = action.payload.time
    },
    setRenderTime: (state, action: PayloadAction<{ component: string; time: number }>) => {
      state.performanceMetrics.renderTimes[action.payload.component] = action.payload.time
    }
  }
})

// Export actions
export const {
  toggleSidebar,
  setSidebarOpen,
  toggleSidebarCollapsed,
  setSidebarCollapsed,
  setTheme,
  setGlobalLoading,
  setLoadingMessage,
  addNotification,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
  setModalData,
  setPageState,
  updatePageState,
  clearPageState,
  setGlobalSearchOpen,
  setGlobalSearchQuery,
  setGlobalSearchResults,
  setGlobalSearchLoading,
  setBreadcrumbs,
  addBreadcrumb,
  removeBreadcrumb,
  clearBreadcrumbs,
  addToNavigationHistory,
  clearNavigationHistory,
  setUserPreferences,
  resetUserPreferences,
  startTour,
  nextTourStep,
  previousTourStep,
  setTourStep,
  completeTour,
  exitTour,
  setDeviceType,
  setScreenSize,
  setPageLoadTime,
  setApiResponseTime,
  setRenderTime
} = uiSlice.actions

// Selectors
export const selectSidebarOpen = (state: { ui: UiState }) => state.ui.sidebarOpen
export const selectSidebarCollapsed = (state: { ui: UiState }) => state.ui.sidebarCollapsed
export const selectTheme = (state: { ui: UiState }) => state.ui.theme
export const selectGlobalLoading = (state: { ui: UiState }) => state.ui.globalLoading
export const selectLoadingMessage = (state: { ui: UiState }) => state.ui.loadingMessage
export const selectNotifications = (state: { ui: UiState }) => state.ui.notifications
export const selectActiveModal = (state: { ui: UiState }) => state.ui.activeModal
export const selectModalData = (state: { ui: UiState }) => state.ui.modalData
export const selectPageState = (page: string) => (state: { ui: UiState }) => state.ui.pageStates[page]
export const selectGlobalSearchOpen = (state: { ui: UiState }) => state.ui.globalSearchOpen
export const selectGlobalSearchQuery = (state: { ui: UiState }) => state.ui.globalSearchQuery
export const selectGlobalSearchResults = (state: { ui: UiState }) => state.ui.globalSearchResults
export const selectGlobalSearchLoading = (state: { ui: UiState }) => state.ui.globalSearchLoading
export const selectBreadcrumbs = (state: { ui: UiState }) => state.ui.breadcrumbs
export const selectNavigationHistory = (state: { ui: UiState }) => state.ui.navigationHistory
export const selectUserPreferences = (state: { ui: UiState }) => state.ui.userPreferences
export const selectActiveTour = (state: { ui: UiState }) => state.ui.activeTour
export const selectTourStep = (state: { ui: UiState }) => state.ui.tourStep
export const selectToursCompleted = (state: { ui: UiState }) => state.ui.toursCompleted
export const selectDeviceType = (state: { ui: UiState }) => state.ui.deviceType
export const selectScreenSize = (state: { ui: UiState }) => state.ui.screenSize
export const selectPerformanceMetrics = (state: { ui: UiState }) => state.ui.performanceMetrics

export default uiSlice.reducer 